import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTimes, FaCheck, FaVideo, FaFileAlt, FaLink, FaSearch, FaFilter, FaExclamationTriangle, FaBuilding } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { Modal, Row, Col, Button, Spinner } from 'react-bootstrap';
import VltFormFields from '../../../common/components/vlt/VltFormFields';
import {
  getInitialVltFormData,
  mapVltToFormData,
  validateVltForm,
  appendVltFormData
} from '../../../common/utils/vltForm';

const VltList = () => {
  const { user } = useAuth();
  const [vlts, setVlts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingVlt, setEditingVlt] = useState(null);
  const [vltToDelete, setVltToDelete] = useState(null);
  const [vltToAssign, setVltToAssign] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterOrganization, setFilterOrganization] = useState('all');
  const [organizations, setOrganizations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [instructors, setInstructors] = useState([]);

  const [formData, setFormData] = useState(getInitialVltFormData());

  const [formErrors, setFormErrors] = useState({});
  const [assignmentData, setAssignmentData] = useState({
    organization_id: '',
    course_id: ''
  });

  useEffect(() => {
    fetchVlts();
    fetchOrganizations();
    fetchGroups();
  }, []);

  useEffect(() => {
    fetchVlts();
  }, [searchTerm, filterStatus, filterType, filterOrganization]);

  const fetchVlts = async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        status: filterStatus !== 'all' ? filterStatus : '',
        type: filterType !== 'all' ? filterType : '',
        organization_id: filterOrganization !== 'all' ? filterOrganization : '',
        limit: 100
      };
      const response = await api.vlt.getAll(params);
      if (response.data.success) setVlts(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch VLTs');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.organizations.getAll();
      if (response.data.success) setOrganizations(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch organizations');
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.groups.list();
      if (response.data.success) setGroups(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch groups');
    }
  };

  const fetchInstructors = async (organizationId = '') => {
    try {
      const response = await api.vlt.getInstructors(organizationId || null);
      if (response.data.success) setInstructors(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch instructors');
      setInstructors([]);
    }
  };

  useEffect(() => {
    if (!showModal) return;
    fetchInstructors(formData.organization_id || '');
  }, [showModal, formData.organization_id]);

  const handleOrganizationChange = (orgId) => {
    setFormData((prev) => ({
      ...prev,
      organization_id: orgId,
      group_ids: orgId ? [] : prev.group_ids,
      instructor_id: ''
    }));
  };

  const handleGroupToggle = (groupId) => {
    setFormData((prev) => {
      const exists = prev.group_ids.includes(groupId);
      return {
        ...prev,
        organization_id: exists ? prev.organization_id : '',
        group_ids: exists
          ? prev.group_ids.filter((id) => id !== groupId)
          : [...prev.group_ids, groupId]
      };
    });
  };

  const filteredVlts = vlts.filter(vlt =>
    vlt.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vlt.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateVlt = () => {
    setEditingVlt(null);
    setFormData(getInitialVltFormData());
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditVlt = (vlt) => {
    setEditingVlt(vlt);
    setFormData(mapVltToFormData(vlt));
    setFormErrors({});
    setShowModal(true);
  };

  const handleTypeChange = (type) => {
    setFormData((prev) => ({
      ...prev,
      type,
      url_or_path: '',
      document_source: 'link',
      document_file: null,
      existing_document_path: '',
      video_source: 'link',
      video_file: null,
      existing_video_path: '',
      meeting_datetime: '',
      instructor_id: ''
    }));
    setFormErrors({});
  };

  const handleDocumentSourceChange = (document_source) => {
    setFormData((prev) => ({
      ...prev,
      document_source,
      url_or_path: '',
      document_file: null
    }));
    setFormErrors({});
  };

  const handleVideoSourceChange = (video_source) => {
    setFormData((prev) => ({
      ...prev,
      video_source,
      url_or_path: '',
      video_file: null
    }));
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateVltForm(formData, { editingVlt });
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setModalLoading(true);
    try {
      const fd = appendVltFormData(formData);

      const response = editingVlt
        ? await api.vlt.update(editingVlt.id, fd)
        : await api.vlt.create(fd);

      if (response.data.success) {
        toast.success(editingVlt ? 'VLT updated!' : 'VLT created!');
        setShowModal(false);
        fetchVlts();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save VLT');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await api.vlt.delete(vltToDelete.id);
      toast.success('VLT deleted');
      setShowDeleteModal(false);
      fetchVlts();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setVltToDelete(null);
    setDeleteLoading(false);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!assignmentData.organization_id && !assignmentData.course_id) {
      toast.error('Select organization or course');
      return;
    }
    setAssignLoading(true);
    try {
      await api.vlt.assign({
        vlt_id: vltToAssign.id,
        organization_id: assignmentData.organization_id || null,
        course_id: assignmentData.course_id || null
      });
      toast.success('VLT assigned!');
      setShowAssignModal(false);
      fetchVlts();
    } catch (error) {
      toast.error('Failed to assign');
    } finally {
      setAssignLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return <FaVideo className="w-5 h-5 text-red-500" />;
      case 'document': return <FaFileAlt className="w-5 h-5 text-blue-500" />;
      case 'link': return <FaLink className="w-5 h-5 text-green-500" />;
      default: return <FaFileAlt className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h4 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Virtual-Led Training
        </h4>
      </div>

      {/* Filters */}
      <div className="mb-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-1">
            <div className="flex rounded-lg overflow-hidden shadow-sm border border-gray-200">
              <div className="flex items-center px-3 bg-gray-50">
                <FaSearch className="text-purple-600" />
              </div>
              <input
                type="text"
                placeholder="Search VLTs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 outline-none"
              />
            </div>
          </div>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
            <option value="all">All Types</option>
            <option value="video">Video</option>
            <option value="document">Document</option>
            <option value="link">Meeting Link</option>
          </select>
          <select value={filterOrganization} onChange={(e) => setFilterOrganization(e.target.value)} className="rounded-lg sm:rounded-xl shadow-sm border-purple-200 focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm form-select">
            <option value="all">All Organizations</option>
            <option value="global">Global only</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
            <button
            onClick={handleCreateVlt}
            className="px-6 py-2  border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold font-semibold rounded-lg shadow-md hover:shadow-xl transition flex items-center gap-2"
          >
            <FaPlus /> Create VLT
          </button>
        </div>
      </div>

      {/* Table */}
      {filteredVlts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-md">
          <FaFileAlt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">No VLTs found</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-2 px-3 text-left">VLT</th>
                <th className="py-2 px-3 text-left">Type</th>
                <th className="py-2 px-3 text-left">Organization</th>
                <th className="py-2 px-3 text-left">Groups</th>
                <th className="py-2 px-3 text-left">Status</th>
                <th className="py-2 px-3 text-left">Created</th>
                <th className="py-2 px-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVlts.map(vlt => (
                <tr key={vlt.id} className="border-b hover:bg-purple-50/50 transition">
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-4">
                
                      <div>
                        <div className="font-semibold text-gray-800">{vlt.title}</div>
                        <div className="text-sm text-gray-600 line-clamp-2">{vlt.description}</div>
                        {/* <div className="text-xs text-purple-600 mt-1">
                          by {vlt.creator?.first_name} {vlt.creator?.last_name}
                        </div> */}
                      </div>
                    </div>
                  </td>
                  <td className="text-left px-3">{getTypeIcon(vlt.type)}</td>
                  <td className="text-left text-gray-800 px-3">
                    {vlt.organization?.name || <span className="text-gray-500">Global</span>}
                  </td>
                  <td className="text-left text-gray-800 px-3">
                    {vlt.organization?.name
                      ? <span className="text-gray-500">—</span>
                      : vlt.groups?.length
                        ? vlt.groups.map((group) => group.name).join(', ')
                        : <span className="text-gray-500">Global</span>}
                  </td>
                  <td className="text-left px-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${vlt.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {vlt.status}
                    </span>
                  </td>
                  <td className="text-left text-gray-600 px-3">
                    {new Date(vlt.createdAt).toLocaleDateString()}
                  </td>
                  <td className="text-left px-3">
                    <div className="flex justify-left gap-2">
                      <button onClick={() => handleEditVlt(vlt)} className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition">
                        <FaEdit className="w-5 h-5" />
                      </button>
                      <button onClick={() => { setVltToDelete(vlt); setShowDeleteModal(true); }} className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition">
                        <FaTrash className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      

      {/* ==================== CREATE / EDIT MODAL ==================== */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered fullscreen="sm-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 px-4 py-3 sm:px-6 sm:py-4">
          <Modal.Title className="text-sm sm:text-base">{editingVlt ? 'Edit VLT' : 'Create New VLT'}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0">
          <form id="vltForm" onSubmit={handleSubmit} className="p-4 sm:p-6">
            <VltFormFields
              formData={formData}
              formErrors={formErrors}
              onChange={setFormData}
              onTypeChange={handleTypeChange}
              onDocumentSourceChange={handleDocumentSourceChange}
              onVideoSourceChange={handleVideoSourceChange}
              onOrganizationChange={handleOrganizationChange}
              onGroupToggle={handleGroupToggle}
              organizations={organizations}
              groups={groups}
              instructors={instructors}
              showOrganization
            />
          </form>
        </Modal.Body>

        <Modal.Footer className="border-0 p-3">
          <Row className="w-100 g-2">
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={() => setShowModal(false)}
                disabled={modalLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-800 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col xs={6}>
              <Button
                bsPrefix="custom-hamburger"
                type="submit"
                form="vltForm"
                disabled={modalLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  editingVlt ? 'Update VLT' : 'Create VLT'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* ==================== DELETE CONFIRM MODAL ==================== */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete VLT</h5>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <strong>{vltToDelete ? vltToDelete.title : ''}</strong>?
          </p>
          <p className="text-sm text-gray-500 mt-3">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button 
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete VLT'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>

      {/* ==================== ASSIGN MODAL ==================== */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <div className="bg-white rounded-3xl shadow-3xl max-w-2xl w-full">
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-8 rounded-t-3xl">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-3xl font-bold">Assign VLT</h2>
                  <p className="text-xl mt-2 opacity-90">"{vltToAssign?.title}"</p>
                </div>
                <button onClick={() => setShowAssignModal(false)} className="p-3 hover:bg-white/20 rounded-full transition">
                  <FaTimes className="w-7 h-7" />
                </button>
              </div>
            </div>

            <form onSubmit={handleAssignSubmit} className="p-8 space-y-7">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-7">
                <div>
                  <label className="flex items-center gap-3 text-lg font-bold text-gray-800 mb-3">
                    <FaBuilding className="text-purple-600" /> Organization
                  </label>
                  <select
                    value={assignmentData.organization_id}
                    onChange={(e) => setAssignmentData({ ...assignmentData, organization_id: e.target.value, course_id: '' })}
                    className="w-full px-5 py-4 rounded-2xl border-2 border-purple-300 focus:border-purple-600 focus:ring-4 focus:ring-purple-200 outline-none text-lg"
                  >
                    <option value="">Select Organization (Optional)</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="flex items-center gap-3 text-lg font-bold text-gray-800 mb-3">
                    <FaBookOpen className="text-purple-600" /> Course
                  </label>
                  <select
                    value={assignmentData.course_id}
                    onChange={(e) => setAssignmentData({ ...assignmentData, course_id: e.target.value, organization_id: '' })}
                    className="w-full px-5 py-4 rounded-2xl border-2 border-purple-300 focus:border-purple-600 focus:ring-4 focus:ring-purple-200 outline-none text-lg"
                  >
                    <option value="">Select Course (Optional)</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.title || course.name}</option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-3">Note: Course selection will override organization</p>
                </div>
              </div>

              <div className="flex justify-end gap-5 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="px-10 py-4 bg-gray-200 text-gray-800 font-bold rounded-2xl hover:bg-gray-300 transition text-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={assignLoading}
                  className="px-12 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-2xl shadow-lg hover:shadow-2xl transition text-lg disabled:opacity-60"
                >
                  {assignLoading ? 'Assigning...' : 'Assign VLT'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VltList;